import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useColorScheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Trans, useTranslation } from 'react-i18next';
import { ApplicationActions, electronics } from '../../utils/constants';
import { isDarkColorScheme } from '../../utils/colorScheme';
import { getBitsElectronicIcon } from '../../utils/getBitsElectronicIcon';
import { useDispatch, useSelector } from 'react-redux';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { RootState } from '../../redux/Store';
import { selectIsProgramActive } from '../../redux/RobotSlice';
import { BprPortIcon } from '../Icons/Bits';
import DistanceSensorIcon from './assets/electrical-explorer/distance-sensor.svg';
import HighSpeedMotorIcon from './assets/electrical-explorer/high-speed-motor.svg';
import HighTorqueMotorIcon from './assets/electrical-explorer/high-torque-motor.svg';
import BeamBreakerIcon from './assets/electrical-explorer/beam-breaker.svg';
import MagneticProximitySwitchIcon from './assets/electrical-explorer/magnetic-proximity-switch.svg';
import AdapterIcon from './assets/electrical-explorer/adapter.svg';

interface ElectronicExplorerItemProps {
  electronic: {
    electronicType: string | null;
    info: {
      devicePartNumber?: string;
      firmwareBootloader?: string;
      firmwareMainApp?: string;
      boardRevision?: number | null;
      formattedUid?: string;
    };
  };
  index: number;
  disableActions?: boolean;
}

const ElectronicExplorerItem = ({ electronic, index, disableActions = false }: ElectronicExplorerItemProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [electronicName, setElectronicName] = useState<string>();
  const { devicePartNumber, firmwareBootloader, firmwareMainApp, boardRevision, formattedUid } = electronic.info;
  const electronicIndex = index + 1;
  const dispatch = useDispatch();
  const isProgramRunning = useSelector((state: RootState) => selectIsProgramActive(state));
  const checkDisabled = isProgramRunning || disableActions;
  const { mode, systemMode } = useColorScheme();
  const isDarkMode = isDarkColorScheme(mode, systemMode);
  const { BEAM_BREAK_CHECK, DISTANCE_SENSOR_CHECK, MAGNETIC_SWITCH_CHECK, MOTOR_CHECK, BPE_ADAPTOR_CHECK } = ApplicationActions.Dialogs;

  useEffect(() => {
    switch (electronic.electronicType) {
      case electronics.distanceSensor.ELECTRONIC_TYPE:
        setElectronicName(t('electronicExplorer.electronic.distance-sensor', 'Distance Sensor'));
        break;
      case electronics.highSpeedMotor.ELECTRONIC_TYPE:
        setElectronicName(t('electronicExplorer.electronic.high-speed-motor', 'High Speed Motor+'));
        break;
      case electronics.highTorqueMotor.ELECTRONIC_TYPE:
        setElectronicName(t('electronicExplorer.electronic.high-torque-motor', 'High Torque Motor+'));
        break;
      case electronics.beamBreakSensor.ELECTRONIC_TYPE:
        setElectronicName(t('electronicExplorer.electronic.beam-break-sensor', 'Beam Break Sensor'));
        break;
      case electronics.magneticSwitch.ELECTRONIC_TYPE:
        setElectronicName(t('electronicExplorer.electronic.magnetic-switch', 'Magnetic Switch'));
        break;
      case electronics.BPEadaptor.ELECTRONIC_TYPE:
        setElectronicName(t('electronicExplorer.electronic.BPE-adaptor', 'BPE Adaptor'));
        break;
      default:
        setElectronicName(t('electronicExplorer.electronic.no-electronic-attached', 'No Electronics Attached'));
        break;
    }
  }, [electronic.electronicType, t]);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleCheckClick = () => {
    switch (electronic.electronicType) {
      case electronics.distanceSensor.ELECTRONIC_TYPE:
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...DISTANCE_SENSOR_CHECK,
            options: { ...DISTANCE_SENSOR_CHECK.options, port: electronicIndex },
          })
        );
        break;
      case electronics.highSpeedMotor.ELECTRONIC_TYPE:
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...MOTOR_CHECK,
            options: { ...MOTOR_CHECK.options, port: electronicIndex, electronicType: electronic.electronicType },
          })
        );
        break;
      case electronics.highTorqueMotor.ELECTRONIC_TYPE:
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...MOTOR_CHECK,
            options: { ...MOTOR_CHECK.options, port: electronicIndex, electronicType: electronic.electronicType },
          })
        );
        break;
      case electronics.beamBreakSensor.ELECTRONIC_TYPE:
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...BEAM_BREAK_CHECK,
            options: { ...BEAM_BREAK_CHECK.options, port: electronicIndex },
          })
        );
        break;
      case electronics.magneticSwitch.ELECTRONIC_TYPE:
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...MAGNETIC_SWITCH_CHECK,
            options: { ...MAGNETIC_SWITCH_CHECK.options, port: electronicIndex },
          })
        );
        break;
      case electronics.BPEadaptor.ELECTRONIC_TYPE:
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...BPE_ADAPTOR_CHECK,
            options: { ...BPE_ADAPTOR_CHECK.options, port: electronicIndex },
          })
        );
        break;
    }
  };

  const getElectronicIcon = () => {
    switch (electronic.electronicType) {
      case electronics.distanceSensor.ELECTRONIC_TYPE:
        return DistanceSensorIcon;
      case electronics.highSpeedMotor.ELECTRONIC_TYPE:
        return HighSpeedMotorIcon;
      case electronics.highTorqueMotor.ELECTRONIC_TYPE:
        return HighTorqueMotorIcon;
      case electronics.beamBreakSensor.ELECTRONIC_TYPE:
        return BeamBreakerIcon;
      case electronics.magneticSwitch.ELECTRONIC_TYPE:
        return MagneticProximitySwitchIcon;
      case electronics.BPEadaptor.ELECTRONIC_TYPE:
        return AdapterIcon;
      default:
        return null;
    }
  };

  const ElectronicIconComponent = getElectronicIcon();
  const CheckButtonIcon = getBitsElectronicIcon(electronic.electronicType);

  return (
    <ListItem sx={{ width: '100%', padding: 0 }}>
      <Box key={index} sx={{ width: '100%' }}>
        <Stack direction={'row'}>
          <ListItemButton disabled={electronic.electronicType === null} onClick={handleClick} sx={{ width: '100%' }}>
            <ListItemIcon>
              <Box
                component={BprPortIcon}
                aria-hidden
                sx={{
                  width: 24,
                  height: 24,
                  display: 'block',
                  opacity: electronic.electronicType === null ? 0.5 : 1,
                  color: 'primary.main',
                }}
              />
            </ListItemIcon>
            <ListItemText>
              <Trans i18nKey="electronicExplorer.electronic.title">
                Port: {{ electronicIndex }} {{ electronicName }}
              </Trans>
            </ListItemText>
          </ListItemButton>
          {electronic.electronicType !== null && CheckButtonIcon ? (
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
              <Tooltip
                title={<Trans i18nKey="programState.unavailableWhileRuntimeBusy">Unavailable while the drive unit is busy</Trans>}
                disableHoverListener={!checkDisabled}
                disableFocusListener={!checkDisabled}
                disableTouchListener={!checkDisabled}
              >
                <span>
                  <Button
                    startIcon={
                      <Box
                        component={CheckButtonIcon}
                        aria-hidden
                        sx={{
                          width: 20,
                          height: 20,
                          display: 'block',
                          color: 'primary.main',
                        }}
                      />
                    }
                    onClick={handleCheckClick}
                    disabled={checkDisabled}
                  >
                    <Trans i18nKey={'electronicExplorerItem.button.check'}>Check</Trans>
                  </Button>
                </span>
              </Tooltip>
              <IconButton onClick={handleClick}>
                <ExpandMore fontSize="large" sx={{ transition: '0.2s', ...(open ? { transform: 'rotate(-180deg)' } : { transform: 'rotate(0)' }) }} />
              </IconButton>
            </Box>
          ) : null}
        </Stack>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box display="flex" sx={{ width: '100%', alignItems: 'center' }}>
            {ElectronicIconComponent && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={ElectronicIconComponent}
                  alt={electronicName}
                  sx={{
                    width: '192px',
                    height: '96px',
                    objectFit: 'contain',
                    filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                  }}
                />
              </Box>
            )}
            <Divider variant="inset" orientation="vertical" flexItem sx={{ marginLeft: '12px', marginRight: '12px', borderRightWidth: '4px' }} />
            <List>
              <ListItemText key={`${electronic.info.devicePartNumber || index}-device-part-number`}>
                <Trans i18nKey="electronicExplorer.electronic.device-part-number" values={{ devicePartNumber: devicePartNumber ?? '' }}>
                  Device P/N: {{ devicePartNumber }}
                </Trans>
              </ListItemText>
              <ListItemText key={`${electronic.info.devicePartNumber || index}-firmware-bootloader`}>
                <Trans i18nKey="electronicExplorer.electronic.firmware-bootloader" values={{ firmwareBootloader: firmwareBootloader ?? '' }}>
                  Firmware Bootloader: {{ firmwareBootloader }}
                </Trans>
              </ListItemText>
              <ListItemText key={`${electronic.info.devicePartNumber || index}-firmware-main-app`}>
                <Trans i18nKey="electronicExplorer.electronic.firmware-main-app" values={{ firmwareMainApp: firmwareMainApp ?? '' }}>
                  Firmware Main App: {{ firmwareMainApp }}
                </Trans>
              </ListItemText>
              <ListItemText key={`${electronic.info.devicePartNumber || index}-board-revision`}>
                <Trans i18nKey="electronicExplorer.electronic.board-revision" values={{ boardRevision: boardRevision ?? '' }}>
                  Board Revision: {{ boardRevision }}
                </Trans>
              </ListItemText>
              <ListItemText key={`${electronic.info.devicePartNumber || index}-uid`}>
                <Trans i18nKey="electronicExplorer.electronic.uid" values={{ uid: formattedUid ?? '' }}>
                  UID: {{ formattedUid }}
                </Trans>
              </ListItemText>
            </List>
          </Box>
        </Collapse>
        <Divider />
      </Box>
    </ListItem>
  );
};

export default ElectronicExplorerItem;
